import { SubmitHandler, useFormContext } from 'react-hook-form';

import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import { CaseQuery } from 'dashboard/models/case-query';
import { CaseDataManagerSchema } from 'dashboard/models/case-template';
import { CaseVariableDefinition } from 'dashboard/models/case-variable-definition';
import { Environment } from 'dashboard/models/environment';
import { ExternalIntegrationDataSource } from 'dashboard/models/external-integration';
import { Tag } from 'dashboard/models/tag';
import { User } from 'dashboard/models/user';
import { Workflow } from 'dashboard/models/workflow';
import { FormAddCaseQueryData } from 'dashboard/views/DashboardCaseTemplatesShowView/CaseQueryTable/FormAddCaseQuery';
import { FormEditCaseQueryData } from 'dashboard/views/DashboardCaseTemplatesShowView/CaseQueryTable/FormEditCaseQuery';
import { CaseTemplateSettingsTabModule } from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateSettings/CaseTemplateSettings';
import { CaseTemplateSettingsFormData } from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateSettings/FormCaseTemplateSettings';
import CaseTemplateSettingsAdvanced from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateSettings/FormCaseTemplateSettings/FormCaseTemplateSettingsAdvanced';
import CaseTemplateSettingsFields from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateSettings/FormCaseTemplateSettings/FormCaseTemplateSettingsFields';
import CaseTemplateSettingsGeneral from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateSettings/FormCaseTemplateSettings/FormCaseTemplateSettingsGeneral';
import CaseTemplateSettingsQueries from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateSettings/FormCaseTemplateSettings/FormCaseTemplateSettingsQueries';
import { FieldSchema } from 'lib/fields';

interface FormCaseTemplateSettingsProps {
  currentEnvironment: Environment;
  initialFieldSchema: FieldSchema[];
  tags: Tag[];
  users: User[];
  actionWorkflows: Workflow[];
  caseQueries: CaseQuery[];
  caseTemplateId: string;
  caseVariableDefinitions: CaseVariableDefinition[];
  caseDataManagerSchema: CaseDataManagerSchema;
  dataSources: ExternalIntegrationDataSource[];
  module: CaseTemplateSettingsTabModule;
  hasCasesBasic: boolean;
  hasCasesEnhanced: boolean;
  hasCasesEnterprise: boolean;
  updateCaseTemplate: (formData: CaseTemplateSettingsFormData) => void;
  handleCreateCaseQuery: (values: FormAddCaseQueryData) => Promise<void>;
  handleUpdateCaseQuery: (values: FormEditCaseQueryData, id: string) => Promise<void>;
}

// TODO: (brandon) clean this up - maybe swap to use the caseTemplate in redux directly like
// we do for LayoutPreview instead of going through the redux-form redux store.
const FormCaseTemplateSettings = ({
  currentEnvironment,
  initialFieldSchema,
  tags,
  users,
  actionWorkflows,
  caseDataManagerSchema,
  caseVariableDefinitions,
  caseTemplateId,
  caseQueries,
  dataSources,
  module,
  hasCasesBasic,
  hasCasesEnhanced,
  hasCasesEnterprise,
  updateCaseTemplate,
  handleCreateCaseQuery,
  handleUpdateCaseQuery,
}: FormCaseTemplateSettingsProps) => {
  const {
    formState: { isDirty },
    handleSubmit,
  } = useFormContext<CaseTemplateSettingsFormData>();

  let subform;

  switch (module) {
    case CaseTemplateSettingsTabModule.General: {
      subform = <CaseTemplateSettingsGeneral users={users} />;
      break;
    }
    case CaseTemplateSettingsTabModule.Advanced: {
      subform = (
        <CaseTemplateSettingsAdvanced
          currentEnvironment={currentEnvironment}
          tags={tags}
          caseQueries={caseQueries}
          dataSources={dataSources}
          actionWorkflows={actionWorkflows}
          caseDataManagerSchema={caseDataManagerSchema}
          hasCasesEnhanced={hasCasesEnhanced}
          hasCasesEnterprise={hasCasesEnterprise}
          handleCreateCaseQuery={handleCreateCaseQuery}
          handleUpdateCaseQuery={handleUpdateCaseQuery}
        />
      );
      break;
    }
    case CaseTemplateSettingsTabModule.Fields: {
      subform = (
        <CaseTemplateSettingsFields
          initialFieldSchema={initialFieldSchema}
          hasCasesBasic={hasCasesBasic}
          hasCasesEnhanced={hasCasesEnhanced}
          hasCasesEnterprise={hasCasesEnterprise}
        />
      );
      break;
    }
    case CaseTemplateSettingsTabModule.Queries: {
      subform = (
        <CaseTemplateSettingsQueries
          caseTemplateId={caseTemplateId}
          caseVariableDefinitions={caseVariableDefinitions}
          hasCasesBasic={hasCasesBasic}
          hasCasesEnhanced={hasCasesEnhanced}
          hasCasesEnterprise={hasCasesEnterprise}
        />
      );
    }
  }

  const onSubmit: SubmitHandler<CaseTemplateSettingsFormData> = (data) => updateCaseTemplate(data);

  return (
    <>
      <NavConfirmPrompt prevent={isDirty} />
      <form onSubmit={handleSubmit(onSubmit)}>{subform}</form>
    </>
  );
};

export default FormCaseTemplateSettings;
